import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Device } from '@/types/device.types';
import { Pencil, Users, Trash2, Check } from 'lucide-react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  SortingState,
  getFilteredRowModel,
  ColumnDef,
} from '@tanstack/react-table';
import { useTableFilters } from '@/hooks/useTableFilters';
import { FilterDialog } from '@/components/shared/FilterDialog';
import { TableHeader } from '@/components/shared/tables/TableHeader/TableHeader';
import { TableRow } from '@/components/shared/tables/TableRow/TableRow';
import { getDeviceTableColumns } from '@/constants/tables/deviceTable.config';
import { TableButton } from '@/components/ui/TableButton';
import { PERMISSIONS } from '@/contexts/PermissionsContext';
import { useDeleteConfirmation } from '@/hooks/useDeleteConfirmation';

interface DeviceTableProps {
  devices: Device[];
  onDeactivate: (id: string) => Promise<void>;
  onReactivate: (id: string) => Promise<void>;
  onEdit: (device: Device) => void;
  onManageUsers: (device: Device) => void;
}

export const DeviceTable: React.FC<DeviceTableProps> = ({
  devices,
  onDeactivate,
  onReactivate,
  onEdit,
  onManageUsers,
}) => {
  const { t } = useTranslation();
  const useDeactivateMode = true;
  const { confirmDelete } = useDeleteConfirmation();
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'serialNumber', desc: false }]);

  const columnConfigs = useMemo(() => getDeviceTableColumns(t), [t]);

  const {
    filterState,
    columnFilters,
    openFilter,
    closeFilter,
    setSearch,
    toggleOption,
    clearFilter,
    applyFilter,
    getFilteredOptions,
  } = useTableFilters(devices, columnConfigs);

  const columnHelper = createColumnHelper<Device>();

  const columns: ColumnDef<Device>[] = useMemo(
    () => [
      ...columnConfigs.map((config) =>
        columnHelper.accessor(config.accessor, {
          id: config.id,
          header: ({ column }) => {
            const filterValue = column.getFilterValue() as string[] | undefined;
            const isFiltered = Array.isArray(filterValue) && filterValue.length > 0;
            return (
              <TableHeader
                header={column}
                columnConfig={config}
                onFilterClick={(columnId) => openFilter(columnId, columnFilters)}
                isFiltered={isFiltered}
              />
            );
          },
          cell:
            config.cell ||
            (({ getValue }) => {
              const value = getValue();
              return value?.toString() || '';
            }),
          enableSorting: config.enableSorting,
          enableColumnFilter: config.enableFilter,
          filterFn: (row, _columnId, filterValue) => {
            if (!Array.isArray(filterValue) || filterValue.length === 0) return true;
            const value = row.getValue(_columnId);
            return filterValue.includes(value?.toString() || '');
          },
        })
      ),
      columnHelper.display({
        id: 'actions',
        header: t('pages.devices.columns.actions'),
        cell: ({ row }) => (
          <div className="flex space-x-2">
            <TableButton
              permission={PERMISSIONS.DEVICE_UPDATE}
              onClick={(e) => {
                e.stopPropagation();
                onEdit(row.original);
              }}
              icon={<Pencil className="h-4 w-4" />}
              label={t('common.edit')}
              variant="primary"
            />

            <TableButton
              permission={PERMISSIONS.DEVICE_ASSOCIATE}
              onClick={(e) => {
                e.stopPropagation();
                onManageUsers(row.original);
              }}
              icon={<Users className="h-4 w-4" />}
              label={t('pages.devices.manageUsers')}
              variant="primary"
            />

            {row.original.status === 'Active' ? (
              <TableButton
                permission={PERMISSIONS.DEVICE_DELETE}
                onClick={(e) => {
                  e.stopPropagation();
                  if (row.original.id) {
                    const deviceName = row.original.serialNumber;
                    confirmDelete(deviceName, 'device', useDeactivateMode, () =>
                      onDeactivate(row.original.id!)
                    );
                  }
                }}
                icon={<Trash2 className="h-4 w-4" />}
                label={t('common.deactivate')}
                variant="danger"
                fixedWidth={true}
              />
            ) : (
              <TableButton
                permission={PERMISSIONS.DEVICE_DELETE}
                onClick={(e) => {
                  e.stopPropagation();
                  if (row.original.id) {
                    onReactivate(row.original.id);
                  }
                }}
                icon={<Check className="h-4 w-4" />}
                label={t('common.activate')}
                variant="primary"
                fixedWidth={true}
              />
            )}
          </div>
        ),
      }),
    ],
    [
      columnConfigs,
      columnHelper,
      t,
      openFilter,
      columnFilters,
      onEdit,
      onManageUsers,
      confirmDelete,
      useDeactivateMode,
      onDeactivate,
      onReactivate,
    ]
  );

  const table = useReactTable({
    data: devices,
    columns,
    state: {
      sorting,
      columnFilters,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const handleRowClick = (deviceId: string | undefined) => {
    if (!deviceId) return;
    setSelectedRowId((current) => (current === deviceId ? null : deviceId));
  };

  return (
    <div className="bg-brand-white rounded-lg shadow-lg">
      {/* Filter Dialogs */}
      {columnConfigs
        .filter((config) => config.enableFilter)
        .map((config) => {
          let filterTitleKey;
          if (config.id === 'vehicle.type') {
            filterTitleKey = 'pages.devices.dialog.vehicle.typeFilterTitle';
          } else if (config.id === 'vehicle.plate') {
            filterTitleKey = 'pages.devices.dialog.vehicle.plateFilterTitle';
          } else {
            filterTitleKey = `pages.devices.dialog.${config.id}FilterTitle`;
          }

          return (
            <FilterDialog
              key={config.id}
              isOpen={!!filterState[config.id]?.isOpen}
              onClose={() => closeFilter(config.id)}
              title={t(filterTitleKey)}
              searchValue={filterState[config.id]?.search || ''}
              onSearchChange={(value) => setSearch(config.id, value)}
              options={getFilteredOptions(config.id)}
              selectedOptions={filterState[config.id]?.selected || []}
              onOptionToggle={(option) => toggleOption(config.id, option)}
              onClear={() => clearFilter(config.id)}
              onApply={() => applyFilter(config.id)}
              getOptionLabel={config.getOptionLabel}
              textCase={config.textCase}
            />
          );
        })}

      {/* Table Container with scroll */}
      <div className="overflow-x-auto">
        <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
          <table className="w-full divide-y divide-gray-200">
            <thead className="sticky top-0 z-10 bg-brand-gray-dark">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-3 py-2 text-left text-xs font-medium text-brand-white tracking-wider"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-gray-200">
              {table.getRowModel().rows.map((row, index) => (
                <TableRow
                  key={row.id}
                  row={row}
                  index={index}
                  selectedRowId={selectedRowId}
                  onRowClick={handleRowClick}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
